import React, { useEffect, useMemo, useState } from 'react'
import { Button, MenuItem } from '@material-ui/core'
import { Checkbox } from '@mui/material'
import moment from 'moment'
import { toast } from 'react-toastify'
import Select from 'app/components/reusable/Select'
import { Loading } from 'app/components/SimpleList/SimpleList'
import HttpHandler, { getModelChangeEvent } from 'app/reusable/HttpHandler'
import { useDismissModal } from 'app/ducks/modal/hooks'

// The `ModelListPage` reports built from roll calls (amsm-backend's
// attendance list endpoints); see `views/attendance/AttendanceReport`.
const ROLL_CALL_REPORT_MODELS = ['attendance-ranking', 'class-occupancy']

const formatClassDate = (date) => moment(date).format('ddd, DD/MM/YYYY')

/**
 * The class roll call ("chamada"), made for the teacher's phone: pick one of
 * the class's recent dates, tap each student present, save once. Saving
 * replaces the whole roll call of that date, so a student marked by mistake
 * can be un-marked. The student list and dates come from the backend
 * (`activities/api/activity-class/:id/roll-call`): enrollments for fixed
 * classes, that date's bookings for group classes.
 */
const ActivityClassRollCall = ({ classId, onSaved }) => {
  const dismissModal = useDismissModal()
  // The date shown in the select, and the one explicitly asked for: the
  // first load asks for none and the backend answers with the latest date.
  const [classDate, setClassDate] = useState(null)
  const [requestedDate, setRequestedDate] = useState(null)
  const [rollCall, setRollCall] = useState(null)
  const [present, setPresent] = useState(new Set())
  const [isLoading, setLoading] = useState(true)
  const [isSaving, setSaving] = useState(false)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    HttpHandler.getActivityClassRollCall(classId, requestedDate)
      .then((data) => {
        if (cancelled || !data) return
        setRollCall(data)
        setPresent(
          new Set(data.students.filter((s) => s.present).map((s) => s.user_id))
        )
        setClassDate(data.class_date)
      })
      .catch(() => {
        if (!cancelled) setRollCall(null)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [classId, requestedDate])

  const students = useMemo(() => rollCall?.students || [], [rollCall])
  const allPresent =
    students.length > 0 && students.every((s) => present.has(s.user_id))

  const toggle = (userId) =>
    setPresent((prev) => {
      const next = new Set(prev)
      if (next.has(userId)) next.delete(userId)
      else next.add(userId)
      return next
    })

  const toggleAll = () =>
    setPresent(allPresent ? new Set() : new Set(students.map((s) => s.user_id)))

  const save = () => {
    setSaving(true)
    toast
      .promise(
        HttpHandler.saveActivityClassRollCall(
          classId,
          classDate,
          Array.from(present)
        ),
        {
          pending: 'Salvando chamada...',
          success: 'Chamada salva com sucesso!',
          error: 'Falha ao salvar chamada',
        }
      )
      .then((data) => {
        // Lists showing attendance (the "Frequência" reports) reload.
        ROLL_CALL_REPORT_MODELS.forEach((model) =>
          window.dispatchEvent(getModelChangeEvent('activities', model))
        )
        if (onSaved) onSaved(data)
        dismissModal()
      })
      .catch(() => {})
      .finally(() => setSaving(false))
  }

  if (!rollCall && isLoading) return <Loading />
  if (!rollCall)
    return <p className="text-muted">Não foi possível carregar a chamada.</p>

  return (
    <div className="t-roll-call">
      <Select
        label="Data da aula"
        name="class_date"
        className="mb-3 t-field-class_date"
        value={classDate || ''}
        onChange={({ target: { value } }) => {
          setClassDate(value)
          setRequestedDate(value)
        }}
      >
        {rollCall.recent_dates.map((date) => (
          <MenuItem key={date} value={date}>
            {formatClassDate(date)}
          </MenuItem>
        ))}
      </Select>

      <div className="flex-between flex-center-v mt-3 mb-2">
        <span className="t-roll-call-summary">
          <b>{present.size}</b> de {students.length} presentes
        </span>
        {students.length > 0 && (
          <Button
            size="small"
            color="primary"
            onClick={toggleAll}
            disabled={isLoading}
          >
            {allPresent ? 'Desmarcar todos' : 'Marcar todos'}
          </Button>
        )}
      </div>

      {isLoading ? (
        <Loading />
      ) : students.length === 0 ? (
        <p className="text-muted my-4">
          {rollCall.activity_class?.is_bookable_class
            ? 'Nenhum aluno agendado para esta data.'
            : 'Nenhum aluno matriculado nesta turma nesta data.'}
        </p>
      ) : (
        students.map((student) => {
          const isPresent = present.has(student.user_id)
          return (
            <label key={student.user_id} style={{ display: 'block' }}>
              <div
                className={`card flex-center-v p-2 mb-1 border-radius-4 t-roll-call-student ${
                  isPresent ? 't-roll-call-present' : ''
                }`}
                style={{
                  cursor: 'pointer',
                  minHeight: 48,
                  // Present rows stand out at a glance on the phone
                  backgroundColor: isPresent
                    ? 'rgba(25, 118, 210, 0.08)'
                    : undefined,
                }}
              >
                <Checkbox
                  checked={isPresent}
                  onChange={() => toggle(student.user_id)}
                  inputProps={{ 'aria-label': student.name }}
                />
                <span>{student.name}</span>
              </div>
            </label>
          )
        })
      )}

      <Button
        fullWidth
        className="mt-3"
        variant="contained"
        color="primary"
        onClick={save}
        disabled={isLoading || isSaving || !classDate}
      >
        Salvar chamada
      </Button>
    </div>
  )
}

export default ActivityClassRollCall
